import type { ManualStatus } from "@/api/db/connection";
import type { ManualTriState } from "@/api/db/manuals";
import { actionError } from "@/lib/error-code";
import type { ChipTone } from "@/lib/labels/shared";

const manualStatusLabel: Record<ManualStatus, string> = {
	draft: "Rascunho",
	delivered: "Entregue",
	revoked: "Revogado",
};

const manualStatusTone: Record<ManualStatus, ChipTone> = {
	draft: "petrol",
	delivered: "teal",
	revoked: "coral",
};

const manualTriStateLabel: Record<ManualTriState, string> = {
	draft: "Rascunho",
	delivered_not_opened: "Entregue sem abertura",
	opened: "Aberto pela paciente",
};

const manualTriStateIconLabel: Record<ManualTriState, string> = {
	draft: "Manual em rascunho",
	delivered_not_opened: "Manual entregue, ainda não aberto",
	opened: "Manual aberto pela paciente",
};

const manualTriStateTone: Record<ManualTriState, ChipTone> = {
	draft: "petrol",
	delivered_not_opened: "amber",
	opened: "teal",
};

const manualErrorLabel: Record<string, string> = {
	GROUP_NOT_EXTRACTABLE: "A consulta ainda não está pronta para gerar o manual.",
	ALREADY_DELIVERED: "O manual já foi entregue.",
	NOT_DELIVERED: "O manual ainda não foi entregue.",
	NOT_FOUND: "Manual não encontrado.",
	UNRESOLVED_FIELD: "O manual tem um campo sem valor.",
};

export const ManualLabels = {
	status: manualStatusLabel,
	statusTone: manualStatusTone,
	triState: manualTriStateLabel,
	triStateIcon: manualTriStateIconLabel,
	triStateTone: manualTriStateTone,
	notOpened: "ainda não aberto",
	openedAt(firstAccessLabel: string) {
		return `aberto ${firstAccessLabel}`;
	},
	accessCount(accessCount: number) {
		return `${accessCount} ${accessCount === 1 ? "acesso" : "acessos"}`;
	},
	drafts: {
		title: "Manuais em rascunho",
		count(total: number) {
			return `${total} aguardando entrega`;
		},
		emptyTitle: "Nenhum manual em rascunho",
	},
	openEditor: "abrir no editor do manual",
	listEmpty: "Nenhum manual para esta pessoa.",
	deliverButton(status: ManualStatus) {
		return status === "revoked" ? "Gerar link novo" : "Gerar link";
	},
	delivering: "gerando link...",
	revokeButton: "Revogar",
	revokeConfirm: "O acesso ao manual será revogado.",
	revoking: "revogando...",
	saved: "Manual salvo.",
	list: {
		title: "Manuais",
		empty: {
			all: {
				title: "Nenhum manual ainda",
			},
			draft: {
				title: "Nenhum manual em rascunho",
			},
			delivered: {
				title: "Nenhum manual entregue",
			},
			not_opened: {
				title: "Nenhum manual aguardando abertura",
			},
			reread: {
				title: "Nenhum manual relido",
			},
			revoked: {
				title: "Nenhum manual revogado",
			},
		},
		error: "Não foi possível carregar os manuais.",
		columnPatient: "Paciente",
		columnStatus: "Status",
		columnDelivered: "Entregue em",
		columnOpened: "Aberto",
		columnAccesses: "Acessos",
		notOpened: "não aberto",
		orphaned: "vínculo perdido",
		none: "-",
		filters: {
			all: "Todos",
			draft: "Rascunhos",
			delivered: "Entregues",
			notOpened: "Não abertos",
			reread: "Relidos",
			revoked: "Revogados",
		},
	},
	weekly: {
		title: "Manuais por semana",
		delivered: "entregues",
		opened: "abertos",
		openRate: "% de abertura",
	},
	error(code: string | undefined) {
		return actionError(manualErrorLabel, code);
	},
};
